import { create } from 'zustand'
import type { CreateSiteRequest, SiteDTO, TestConnectionResponse } from '@shared/types'
import { api, errorMessage } from '@/lib/api'

export interface SitePatch {
  sshKeyId?: string
}

interface SitesState {
  sites: SiteDTO[]
  loading: boolean
  error: string | null

  load: () => Promise<void>
  /** Omit `sshKeyId` to use the instance's configured WP Engine key. */
  create: (installName: string, sshKeyId?: string) => Promise<SiteDTO>
  test: (installName: string, sshKeyId?: string) => Promise<TestConnectionResponse>
  remove: (id: string) => Promise<void>
  update: (id: string, patch: SitePatch) => Promise<SiteDTO>
  upsertFromEvent: (siteId: string, patch: Partial<SiteDTO>) => void
}

export const useSitesStore = create<SitesState>((set, get) => ({
  sites: [],
  loading: false,
  error: null,

  load: async () => {
    set({ loading: true, error: null })
    try {
      const sites = await api.get<SiteDTO[]>('/sites')
      set({ sites, loading: false })
    } catch (error) {
      set({ loading: false, error: errorMessage(error) })
    }
  },

  create: async (installName, sshKeyId) => {
    // Built conditionally rather than passing `sshKeyId: undefined`: the server
    // distinguishes "omitted, use the instance key" from a present-but-invalid value,
    // and relying on JSON.stringify to drop the undefined would make that implicit.
    const body: CreateSiteRequest = sshKeyId ? { installName, sshKeyId } : { installName }
    const site = await api.post<SiteDTO>('/sites', body)
    // The list is newest-first, and this is the newest.
    set((state) => ({ sites: [site, ...state.sites.filter((s) => s.id !== site.id)] }))
    return site
  },

  test: async (installName, sshKeyId) =>
    api.post<TestConnectionResponse>(
      '/sites/test',
      sshKeyId ? { installName, sshKeyId } : { installName }
    ),

  remove: async (id) => {
    await api.del<void>(`/sites/${id}`)
    set((state) => ({ sites: state.sites.filter((site) => site.id !== id) }))
  },

  update: async (id, patch) => {
    const site = await api.patch<SiteDTO>(`/sites/${id}`, patch)
    set((state) => ({ sites: state.sites.map((s) => (s.id === id ? site : s)) }))
    return site
  },

  upsertFromEvent: (siteId, patch) => {
    // Events carry deltas, not whole rows, so a site we have never listed cannot
    // be synthesised here — `load()` fills that gap.
    if (!get().sites.some((site) => site.id === siteId)) return
    set((state) => ({
      sites: state.sites.map((site) => (site.id === siteId ? { ...site, ...patch } : site))
    }))
  }
}))
